import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Cloud, MapPin } from "lucide-react";
import Seo, { breadcrumbSchema, faqSchema } from "../components/Seo";
import BookCallButton from "../components/BookCallButton";
import { Reveal } from "../components/common";
import { XERO_LOCATIONS } from "../data/xeroLocations";
import { getLocation } from "../data/locations";

/**
 * The UK-wide Xero hub (decision D). It deliberately targets the searches with no
 * town in them — "xero accountants", "xero bookkeepers", "xero platinum partner" —
 * and sends town searches down to the town pages, which already rank for them.
 */

const WORK = [
  {
    title: "Moving you onto Xero",
    to: "/services/set-up-and-implementation",
    body: "Migration from Sage, QuickBooks, FreeAgent or spreadsheets, with the history brought across and every opening balance reconciled. The old system stays readable until you are comfortable.",
  },
  {
    title: "Xero support and training",
    to: "/services/support#xero-training",
    body: "Someone to ask when something does not look right — bank feeds, reconciliation, a transaction nobody can explain — and your team shown how to use the parts of Xero they actually need. Questions are part of the fee, not billed by the call.",
  },
  {
    title: "Clean-up and troubleshooting",
    to: "/services/troubleshooting",
    body: "Files that have drifted — unreconciled months, suspense accounts, duplicated feeds — brought back to a position you can trust and report from.",
  },
  {
    title: "Xero subscriptions and pricing",
    to: "/services/xero-subscription",
    body: "What the plans cost, which one fits, and what changes if you add payroll or projects. Priced plainly, with no commission dressed up as advice.",
  },
  {
    title: "The bookkeeping itself",
    to: "/services/bookkeeping",
    body: "For most clients we do not just support Xero, we run it: records kept current, VAT and payroll filed, and reporting built on top.",
  },
];

const FAQS = [
  {
    question: "Do you work with businesses across the UK?",
    answer:
      "Yes. Xero is the reason we can. The file lives in the cloud, so we work in the same records you do wherever you are — most of our clients are in the Midlands because that is where our office is, but distance makes no difference to how the work is done. We have town pages for the Midlands because local search brings people in, not because we only work there.",
  },
  {
    question: "What does being a Xero Platinum Partner actually mean?",
    answer:
      "Platinum is Xero's top partner tier, based on the number of clients a practice runs on Xero and the certifications its team holds. In practice it means we see the awkward cases often enough to recognise them, and we have a direct line into Xero when something needs escalating. It is a measure of depth, not a discount.",
  },
  {
    question: "Are you only for businesses already on Xero?",
    answer:
      "No — about half the conversations we have start somewhere else: desktop Sage, QuickBooks, a spreadsheet, or a bookkeeper who has retired. Migration is routine work for us, and we will tell you honestly if the timing is wrong, which is usually mid-VAT-quarter or mid-year-end.",
  },
  {
    question: "Can we use you for Xero support but keep our own accountant?",
    answer:
      "Sometimes, and we will be straight about when it works. Support, training and clean-up can sit alongside another firm. What we do not do is compliance in isolation — filing returns against records nobody maintains — so if your accountant is only filing once a year, the support will keep finding the same problems.",
  },
  {
    question: "Why are you a 100% Xero practice?",
    answer:
      "Because supporting several platforms badly serves nobody. Every client on one platform means every client's records are current, we see the same numbers you do, and our whole team knows the software properly rather than passably. It is a deliberate constraint, and it is the reason the support is any good.",
  },
  {
    question: "What does it cost?",
    answer:
      "A fixed monthly fee agreed before we start. Build your package on the pricing page and you will see your own figure in about two minutes, with no email address needed.",
  },
];

export default function XeroAccountantsHub() {
  const towns = XERO_LOCATIONS.map((x) => ({ ...x, town: getLocation(x.baseSlug)?.town })).filter((x) => x.town);

  return (
    <div data-testid="xero-hub">
      <Seo
        title="Xero Accountants — Xero Platinum Partner, UK-Wide"
        description="Xero accountants and bookkeepers working UK-wide. A Xero Platinum Partner and a 100% Xero practice: migration, support, training, clean-up and the bookkeeping itself, on fixed monthly fees."
        jsonLd={[
          faqSchema(FAQS),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Xero Accountants", url: "/xero-accountants" },
          ]),
        ]}
      />

      <section className="bg-navy text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-20 md:py-28">
          <Reveal>
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider">
              <Cloud size={14} className="text-orange" /> Xero Platinum Partner
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mt-6">Xero Accountants</h1>
            <p className="text-slate-300 text-lg mt-6 max-w-2xl leading-relaxed">
              CASS is a Xero Platinum Partner and a 100% Xero practice, working with businesses across the UK.
              Every client we have runs on Xero — which is why the support is any good, and why we can see the
              same numbers you do rather than waiting for records to be sent over.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-9">
              <BookCallButton testId="xero-hub-book-call" label="Request a Discovery Call"
                className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-[1.15rem] rounded-full transition-all hover:-translate-y-0.5" />
              <Link to="/pricing" data-testid="xero-hub-pricing"
                className="inline-flex items-center gap-2 border-2 border-white/25 hover:border-white text-white font-bold px-8 py-[1.15rem] rounded-full transition-colors">
                Build Your Indicative Price <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16" data-testid="xero-hub-intro">
        <h2 className="text-2xl md:text-3xl font-extrabold text-navy max-w-3xl">A practice built around one platform</h2>
        <p className="text-slate-600 text-lg leading-relaxed max-w-3xl mt-5">
          Plenty of firms say they "work with Xero". We only work with Xero. Every client, every file, one
          platform — so the whole team knows the software properly, the records are current rather than
          reconstructed at the year end, and nobody has to export anything to answer a question.
        </p>
        <p className="text-slate-600 text-lg leading-relaxed max-w-3xl mt-5">
          Platinum is Xero's top partner tier, awarded on the number of clients a practice runs on Xero and the
          certifications its team holds. What it means for you is simply that the awkward cases are familiar
          ones.
        </p>
        <div className="grid sm:grid-cols-3 gap-4 mt-9">
          {["Xero Platinum Partner", "A 100% Xero practice", "ACSP Registered and ICB Licenced"].map((p) => (
            <div key={p} className="flex items-start gap-2.5 bg-slate-50 rounded-xl border border-slate-100 p-4">
              <Check size={18} className="text-orange shrink-0 mt-0.5" />
              <span className="text-sm font-medium text-navy leading-snug">{p}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 py-16 md:py-20" data-testid="xero-hub-work">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl md:text-3xl font-extrabold text-navy">What we do with Xero</h2>
          <p className="text-slate-600 leading-relaxed max-w-3xl mt-4">
            Most businesses arrive needing one of these and end up with several.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mt-9">
            {WORK.map((w) => (
              <Link key={w.to} to={w.to} data-testid={`xero-hub-work-${w.to.split("/").pop()}`}
                className="group bg-white rounded-2xl border border-slate-200 p-6 hover:border-orange hover:-translate-y-0.5 transition-all">
                <span className="inline-flex items-center gap-2 font-display font-bold text-navy">
                  {w.title} <ArrowRight size={15} className="text-orange opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
                <p className="text-sm text-slate-600 leading-relaxed mt-2">{w.body}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16" data-testid="xero-hub-remote">
        <h2 className="text-2xl md:text-3xl font-extrabold text-navy max-w-3xl">How it works when we are not down the road</h2>
        <p className="text-slate-600 text-lg leading-relaxed max-w-3xl mt-5">
          Our office is in Amington, Tamworth, and a good number of clients have never been to it. Everything
          runs in live Xero, so you and we are looking at the same file at the same time. Bills and receipts
          arrive through Dext, reviews happen on video, and you have a named client manager rather than a
          helpdesk queue. Where meeting in person genuinely helps — a migration kick-off, a difficult year — we
          travel.
        </p>
      </section>

      <section className="bg-slate-50 py-16" data-testid="xero-hub-towns">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl md:text-3xl font-extrabold text-navy">Looking for a Xero accountant near you?</h2>
          <p className="text-slate-600 leading-relaxed max-w-3xl mt-4">
            We work UK-wide, and these towns have a page of their own because that is where we are asked for
            most often — including the option of meeting face to face.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-8">
            {towns.map((t) => (
              <Link key={t.slug} to={`/${t.slug}`} data-testid={`xero-hub-town-${t.slug}`}
                className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between hover:border-orange hover:-translate-y-0.5 transition-all group">
                <span className="inline-flex items-center gap-2 font-semibold text-navy text-sm">
                  <MapPin size={15} className="text-orange" /> Xero accountants in {t.town}
                </span>
                <ArrowRight size={16} className="text-slate-300 group-hover:text-orange" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16" data-testid="xero-hub-faqs">
        <h2 className="text-2xl md:text-3xl font-extrabold text-navy">Common questions</h2>
        <div className="mt-7 space-y-3">
          {FAQS.map((f, i) => (
            <div key={i} className="bg-slate-50 rounded-2xl border border-slate-200 p-6" data-testid={`xero-hub-faq-${i}`}>
              <h3 className="font-display font-bold text-navy">{f.question}</h3>
              <p className="text-slate-600 leading-relaxed mt-2">{f.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-navy text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold">Get more out of Xero than a login</h2>
          <p className="text-slate-300 mt-5 max-w-2xl mx-auto leading-relaxed">
            Tell us where you are now — on Xero and frustrated, on something else and thinking about moving, or
            starting from scratch — and we will tell you honestly what we would do about it.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-9">
            <BookCallButton testId="xero-hub-cta-call" label="Request a Discovery Call"
              className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-[1.15rem] rounded-full transition-all hover:-translate-y-0.5" />
            <Link to="/pricing" data-testid="xero-hub-cta-pricing"
              className="inline-flex items-center gap-2 border-2 border-white/25 hover:border-white text-white font-bold px-8 py-[1.15rem] rounded-full transition-colors">
              Build Your Indicative Price <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
